<template>
  <EMCard class="custom-props" :class="sectionClasses" :border-hover="false">
    <div v-if="title || subtitle || $slots.header" class="section-header">
      <div class="section-title-group">
        <h3 v-if="title" class="section-title">{{ title }}</h3>
        <p v-if="subtitle" class="section-subtitle">{{ subtitle }}</p>
      </div>
      <div v-if="$slots.header" class="section-header-actions">
        <slot name="header" />
      </div>
    </div>

    <div class="section-content" :class="contentClasses">
      <slot />
    </div>

    <div v-if="$slots.footer" class="section-footer">
      <slot name="footer" />
    </div>
  </EMCard>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import EMCard from '@/common/components/ui/card/EMCard.vue';

interface Props {
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'outlined' | 'filled';
  noPadding?: boolean;
  noBackground?: boolean;
  columns?: 1 | 2 | 3 | 4;
  gap?: 'small' | 'medium' | 'large';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  noPadding: false,
  noBackground: false,
  columns: 1,
  gap: 'medium',
});

const slots = useSlots();

const sectionClasses = computed(() => [
  `section-variant-${props.variant}`,
  {
    'no-padding': props.noPadding,
    'no-background': props.noBackground,
  },
]);

const contentClasses = computed(() => [
  `section-columns-${props.columns}`,
  `section-gap-${props.gap}`,
]);
</script>

<style scoped>
.custom-props {
  padding: 30px;
  height: 100%;

  /* border: none !important; */
}

/* Override EMCard's overflow:hidden on content to allow dropdowns to escape */
.custom-props :deep(.em-card-content) {
  overflow: visible;
}

.em-form-section.no-padding {
  padding: 0;
}

.em-form-section.no-background {
  background: transparent;
  border: none;
}

/* Variants */
.em-form-section.section-variant-filled {
  background: var(--ion-background-color-step-50, #f5f5f5);
  border: none;
}

.em-form-section.section-variant-outlined {
  background: transparent;
}

/* Header */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.section-title-group {
  flex: 1;
}

.section-title {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 500;
  color: var(--ion-color-primary-dark, #1a365d);
}

.section-subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--ion-text-color-secondary, #666);
}

.section-header-actions {
  flex-shrink: 0;
}

/* Content */
.section-content {
  display: grid;
  width: 100%;
}

.section-content.section-columns-1 {
  grid-template-columns: 1fr;
}

.section-content.section-columns-2 {
  grid-template-columns: repeat(2, 1fr);
}

.section-content.section-columns-3 {
  grid-template-columns: repeat(3, 1fr);
}

.section-content.section-columns-4 {
  grid-template-columns: repeat(4, 1fr);
}

/* Gap */
.section-content.section-gap-small {
  gap: 12px;
}

.section-content.section-gap-medium {
  gap: 20px;
}

.section-content.section-gap-large {
  gap: 32px;
}

/* Footer */
.section-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--ion-border-color, #e0e0e0);
}

/* Responsive */
@media (max-width: 768px) {
  .section-content.section-columns-2,
  .section-content.section-columns-3,
  .section-content.section-columns-4 {
    grid-template-columns: 1fr;
  }
}
</style>
